import { AlertCircle, FileText, Pencil, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';
import SuccessModal from '../../../components/ConfirmModal/SuccessModal';
import Loader from '../../../components/Loader/Loader';
import { deleteShiftLog, downloadShiftReport, getShiftLog } from '../../../services/superAdminServices';
import ShiftModal from './AddShiftModal';
import DashboardHeader from './DashboardHeader';
import EventModal from './NewShiftModal';
import Pagination from '../../../components/Pagination/Pagination';
import { useProfile } from '../../../Contexts/ProfileContext';
import { isReadOnlyShiftLogRole } from '../../../constants/roles';

// import { useAuth } from '../../Components/Providers/Auth/AuthContext';

interface ShiftLog {
  id: number;
  reference_number: string;
  date: string;

  start_time: string;
  end_time: string;

  department: string | null;
  priority: string;
  location: string | null;
  status: string;

  created_by: string;
  unit_on_load: string;
  description: string;
  handover_remarks: string | null;

  attendees: {
    id: number;
    shift_log_id: number;
    user_id: number;
    designation: string | null;
    user: {
      id: number;
      name: string;
      email: string;
      role: string;
    };
  }[];
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'On Shift':
      return 'text-pink-600';
    case 'New':
      return 'text-blue-600';
    case 'Signed off':
      return 'text-green-600';
    default:
      return 'text-gray-600';
  }
};

const ShiftLogBooks = () => {
  const navigate = useNavigate();
  const profile: any = useProfile();
  const readOnly = isReadOnlyShiftLogRole(profile?.role);

  const [shiftLogs, setShiftLogs] = useState<ShiftLog[]>([]);

  const [loading, setLoading] = useState(true);
  const [eventModalFlag, setEventModalFlag] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null);

  const [showSuccess, setShowSuccess] = useState(false);
  const [showEditSuccess, setShowEditSuccess] = useState(false);

  const [isEditMode, setIsEditMode] = useState(false);

  const [apiError, setApiError] = useState('');
  const [showError, setShowError] = useState(false);
  const [downloadingShiftId, setDownloadingShiftId] = useState<number | null>(null);

  const [searchQuery, setSearchQuery] = useState('');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // meta.from is the exact 1-based starting row number the API already
  // calculated for this page — more reliable than computing an offset
  // from per_page, since it correctly accounts for the last page having
  // fewer rows and any custom/variable page sizes.
  const [fromIndex, setFromIndex] = useState(1);

  const requestIdRef = useRef(0);

  const handleDownload = async (item: ShiftLog) => {
    try {
      setDownloadingShiftId(item.id);

      const pdfBlob = await downloadShiftReport(item.id);
      const url = window.URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', `${item.reference_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download failed:', error);
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to download report.';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setDownloadingShiftId(null);
    }
  };

  const fetchLogs = async (page = 1, search = '') => {
    const requestId = ++requestIdRef.current;
    try {
      const data = await getShiftLog(page, search);

      // Ignore this response if a newer request has since been fired
      if (requestId !== requestIdRef.current) return;

      const logs = data?.data || [];
      const visibleLogs = readOnly
        ? logs.filter((log: ShiftLog) => log.attendees?.some((attendee) => Number(attendee.user_id) === Number(profile?.id)))
        : logs;

      setShiftLogs(visibleLogs);
      setCurrentPage(data?.meta?.current_page || 1);
      setTotalPages(data?.meta?.last_page || 1);
      setFromIndex(data?.meta?.from || 1);
    } catch (err: any) {
      if (requestId !== requestIdRef.current) return;
      console.error(err);
      const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to load shift logs';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1); // triggers the fetch effect below
      } else {
        fetchLogs(1, searchQuery);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  // Fetch whenever the page changes, using whatever search query is active
  useEffect(() => {
    fetchLogs(currentPage, searchQuery);
  }, [currentPage]);

  if (loading) return <Loader />;

  return (
    <div className="overflow-x-auto p-6 bg-white">
      <DashboardHeader
        readOnly={readOnly}
        onShiftCreated={() => {
          fetchLogs(currentPage, searchQuery);
        }}
        onSearch={(query) => {
          // setCurrentPage(1);
          setSearchQuery(query);
        }}
      />
      <table className="min-w-full text-sm text-left border rounded shadow">
        <thead className="text-gray-500 text-xs">
          <tr className="h-[45px]">
            <th className="px-4 py-1 border text-xs font-medium text-gray-500">Sl. No</th>
            <th className="px-4 py-1 border text-xs font-medium text-gray-500">Ref No.</th>
            <th className="px-4 py-1 border text-xs font-medium text-gray-500">Created Date</th>
            <th className="px-4 py-1 border text-xs font-medium text-gray-500">Start Time</th>
            <th className="px-4 py-1 border text-xs font-medium text-gray-500"> End Time</th>
            <th className="px-4 py-1 border text-xs font-medium text-gray-500">Created By</th>
            <th className="px-4 py-1 border text-xs font-medium text-gray-500">Unit On Load</th>
            {/*<th className="px-4 py-1 border text-xs font-medium text-gray-500">Attendees</th>*/}
            {/* <th className="px-4 py-1 border text-xs font-medium text-gray-500">Department</th> */}
            {/*<th className="px-4 py-1 border text-xs font-medium text-gray-500">Priority</th>*/}
            {/* <th className="px-4 py-1 border text-xs font-medium text-gray-500">Location</th> */}
            <th className="px-4 py-1 border text-xs font-medium text-gray-500">Status</th>
            <th className="px-4 py-1 border text-xs font-medium text-gray-500">Actions</th>
          </tr>
        </thead>
        <tbody>
          {shiftLogs.length === 0 && (
            <tr>
              <td colSpan={9} className="px-4 py-10 text-center text-sm text-gray-500 border">
                {readOnly ? 'No shift logs are assigned to you.' : 'No shift logs found.'}
              </td>
            </tr>
          )}
          {shiftLogs?.map((item, index) => (
            <tr key={item?.id} className="cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/ShiftLogbook/${item.id}`)}>
              <td className="px-4 py-2 text-gray-500 border text-center">{fromIndex + index}</td>
              <td className="px-4 py-2 border font-medium">{item?.reference_number}</td>
              <td className="px-4 py-2 border font-medium">{item?.date ? new Date(item.date).toLocaleDateString('en-GB') : '-'}</td>
              <td className="px-4 py-2 border">{item?.start_time || '-'}</td>
              <td className="px-4 py-2 border">{item?.end_time || '-'}</td>
              <td className="px-4 py-2 border">{item?.created_by || '-'}</td>
              <td className="px-4 py-2 border">{item?.unit_on_load || '-'}</td>
              {/*<td className="px-4 py-2 border">{item?.attendees?.length ? item.attendees.map((att) => att.user.name).join(', ') : '-'}</td>*/}
              {/* <td className="px-4 py-2 border font-medium">{item?.department}</td> */}
              {/*<td className="px-4 py-2 border font-medium">{item?.priority}</td>*/}
              {/* <td className="px-4 py-2 border font-medium">{item?.location}</td> */}
              <td className="px-4 py-2 border font-semibold">
                <span className={getStatusColor(item?.status)}>● {item.status}</span>
              </td>
              <td className="px-4 py-2 border">
                {item.status?.toLowerCase() === 'signed off' ? (
                  <button
                    type="button"
                    title="Download shift report"
                    aria-label={`Download report for ${item.reference_number}`}
                    disabled={downloadingShiftId === item.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(item);
                    }}
                    className="flex items-center justify-center text-[#7C91AC] hover:text-[#627B9A] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <FileText size={28} className={downloadingShiftId === item.id ? 'animate-pulse' : ''} />
                  </button>
                ) : readOnly ? (
                  <span className="text-xs text-gray-400">View only</span>
                ) : (
                  <div className="flex items-center space-x-3">
                    {/* Delete */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteId(item.id);
                        setIsDeleteOpen(true);
                      }}
                      className="text-red-600"
                    >
                      <Trash2 size={18} />
                    </button>

                    {/* Edit */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedShiftId(item.id);
                        setIsEditMode(true);
                      }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Pencil size={18} />
                    </button>

                    {/* Add Event */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedShiftId(item.id);
                        setEventModalFlag(true);
                      }}
                      className="w-8 h-8 flex items-center justify-center rounded-full border border-black text-black hover:bg-blue-100"
                    >
                      +
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <ConfirmModal
        isOpen={isDeleteOpen}
        title="Are you sure you want to delete this shift log?"
        message=""
        confirmText={isDeleting ? 'Deleting...' : 'Yes, Delete'}
        loadingText="Deleting.."
        isLoading={isDeleting}
        cancelText="Cancel"
        onCancel={() => {
          if (isDeleting) return;
          setIsDeleteOpen(false);
          setDeleteId(null);
        }}
        onConfirm={async () => {
          if (deleteId === null) return;

          setIsDeleting(true);
          try {
            await deleteShiftLog(deleteId);
            setIsDeleteOpen(false);
            setDeleteId(null);
            setShowDeleteSuccess(true);
            setTimeout(() => setShowDeleteSuccess(false), 3000);
            fetchLogs(currentPage, searchQuery);
          } catch (error: any) {
            console.error('Error deleting shift log:', error);
            const message =
              error?.response?.data?.message ||
              error?.response?.data?.error ||
              error?.message ||
              'Failed to delete shift log. Please try again.';
            setIsDeleteOpen(false);
            setDeleteId(null);
            setApiError(message);
            setShowError(true);
            setTimeout(() => setShowError(false), 5000);
          } finally {
            setIsDeleting(false);
          }
        }}
      />

      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      {eventModalFlag && selectedShiftId !== null && (
        <EventModal
          onClose={() => setEventModalFlag(false)}
          selectedShiftId={selectedShiftId}
          onSuccess={() => {
            setShowSuccess(true);
            fetchLogs(currentPage, searchQuery);
          }}
        />
      )}

      {isEditMode && selectedShiftId !== null && (
        <ShiftModal
          shiftId={selectedShiftId}
          isEdit={true}
          onClose={() => {
            setIsEditMode(false);
            setSelectedShiftId(null);
          }}
          onSave={() => {
            setIsEditMode(false);
            setSelectedShiftId(null);
            setShowEditSuccess(true);
            fetchLogs(currentPage, searchQuery);
          }}
        />
      )}

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Event Added"
        message="The event has been successfully logged."
        buttonText="OK"
        onButtonClick={() => setShowSuccess(false)}
      />

      <SuccessModal
        isOpen={showDeleteSuccess}
        onClose={() => setShowDeleteSuccess(false)}
        title="Deleted Successfully"
        message="The shift log has been deleted successfully."
        buttonText="OK"
        onButtonClick={() => setShowDeleteSuccess(false)}
      />

      <SuccessModal
        isOpen={showEditSuccess}
        onClose={() => setShowEditSuccess(false)}
        title="Updated Successfully"
        message="The shift log has been updated successfully."
        buttonText="OK"
        onButtonClick={() => setShowEditSuccess(false)}
      />

      {/* Error Notification */}
      {showError && (
        <div className="fixed bottom-6 right-6 z-[100]">
          <div className="bg-red-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[300px] max-w-[500px] relative animate-slide-in">
            <button onClick={() => setShowError(false)} className="absolute top-3 right-3 text-white hover:text-gray-200 transition-colors">
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Error</h4>
                <p className="text-sm">{apiError}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftLogBooks;
